import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, RefreshCw, Download, Smartphone } from "lucide-react";

/**
 * Admin 2FA (TOTP / Google Authenticator) manager.
 * Flow:
 *   1. idle (current status fetched from /auth/me)
 *   2. setup: backend generates secret + QR. We DO NOT persist until the user
 *      proves the scan by entering a valid 6-digit code.
 *   3. enabled: shows recovery codes ONCE; after dismissal, only a "Regenerate" path remains.
 */
export default function TOTPSection({ enabled, recoveryRemaining, onChange }) {
  const [step, setStep] = useState("idle"); // idle | setup | verifying | show_codes | disabling | regenerating
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, qr_png, account, issuer }
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [codesShown, setCodesShown] = useState([]);
  const [loading, setLoading] = useState(false);

  const begin = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/totp/setup");
      setSetupData(r.data);
      setStep("setup");
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar 2FA");
    } finally { setLoading(false); }
  }, []);

  const enable = useCallback(async () => {
    if (!setupData || code.length !== 6) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/totp/enable", { secret: setupData.secret, token: code });
      setCodesShown(r.data.recovery_codes || []);
      setStep("show_codes");
      setCode("");
      setSetupData(null);
      toast.success("2FA ativada com sucesso");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido");
    } finally { setLoading(false); }
  }, [setupData, code, onChange]);

  const disable = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/auth/totp/disable", {
        password,
        token: code && code.length === 6 ? code : undefined,
      });
      toast.success("2FA desativada");
      setStep("idle");
      setPassword("");
      setCode("");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao desativar");
    } finally { setLoading(false); }
  }, [password, code, onChange]);

  const regenerate = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/totp/recovery-codes/regenerate", {
        password, token: code,
      });
      setCodesShown(r.data.recovery_codes || []);
      setStep("show_codes");
      setCode("");
      setPassword("");
      toast.success("Novos códigos gerados");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    } finally { setLoading(false); }
  }, [password, code, onChange]);

  const copyCodes = () => {
    navigator.clipboard.writeText(codesShown.join("\n")).then(
      () => toast.success("Códigos copiados"),
      () => toast.error("Não foi possível copiar"),
    );
  };

  const downloadCodes = () => {
    const content =
`CEO DEMoraes — Códigos de recuperação 2FA
Gerado em: ${new Date().toLocaleString("pt-BR")}

Guarde estes códigos em local seguro. Cada um pode ser usado UMA vez
caso você perca o acesso ao Google Authenticator.

${codesShown.map((c, i) => `${(i + 1).toString().padStart(2, "0")}. ${c}`).join("\n")}
`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ceo-demoraes-2fa-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ---------- Render ----------
  if (step === "show_codes") {
    return (
      <div className="space-y-4" data-testid="totp-recovery-codes-display">
        <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <ShieldCheck className="size-5 text-amber-500 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-foreground">Salve estes códigos agora</div>
            <div className="text-xs text-muted-foreground mt-1">
              Eles serão mostrados <span className="font-semibold">apenas uma vez</span>. Cada código
              permite 1 login caso você perca o Authenticator. Guarde em gerenciador de senhas
              ou imprima.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 bg-muted/20 border border-border rounded p-4 font-mono text-sm" data-testid="totp-codes-grid">
          {codesShown.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs w-6">{String(i + 1).padStart(2, "0")}</span>
              <span className="tracking-wider">{c}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={copyCodes} data-testid="totp-copy-codes-btn">
            <Copy className="size-4 mr-1" /> Copiar todos
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCodes} data-testid="totp-download-codes-btn">
            <Download className="size-4 mr-1" /> Baixar .txt
          </Button>
          <Button size="sm" onClick={() => { setCodesShown([]); setStep("idle"); }} data-testid="totp-codes-done-btn">
            Já guardei — continuar
          </Button>
        </div>
      </div>
    );
  }

  if (step === "setup" && setupData) {
    return (
      <div className="space-y-4" data-testid="totp-setup-panel">
        <div className="flex items-start gap-3">
          <Smartphone className="size-5 text-primary mt-1" />
          <div className="text-sm text-muted-foreground">
            Abra o <span className="font-semibold text-foreground">Google Authenticator</span> (ou Authy, 1Password etc.),
            escaneie o QR code abaixo e informe o código de 6 dígitos gerado.
          </div>
        </div>
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="bg-white p-3 rounded border border-border" data-testid="totp-qr-code">
            <img src={setupData.qr_png} alt="QR code 2FA" width={200} height={200} />
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="label-tech">Chave manual (se não conseguir escanear)</Label>
              <div className="font-mono text-xs bg-muted/20 border border-border rounded p-2 break-all" data-testid="totp-manual-secret">
                {setupData.secret}
              </div>
            </div>
            <div>
              <Label className="label-tech">Código do Authenticator</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="font-mono text-center text-xl tracking-[0.4em]"
                data-testid="totp-verify-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={enable} disabled={loading || code.length !== 6} data-testid="totp-confirm-enable-btn">
                {loading ? "Verificando..." : "Ativar 2FA"}
              </Button>
              <Button variant="outline" onClick={() => { setStep("idle"); setSetupData(null); setCode(""); }} data-testid="totp-cancel-setup-btn">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "disabling") {
    return (
      <div className="space-y-3 border border-destructive/30 rounded p-4 bg-destructive/5" data-testid="totp-disable-panel">
        <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
          <ShieldOff className="size-4" /> Desativar 2FA
        </div>
        <p className="text-xs text-muted-foreground">Confirme sua senha e (opcionalmente) um código 2FA atual para desativar a verificação em duas etapas.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Input type="password" placeholder="Senha atual" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="totp-disable-pwd-input" />
          <Input
            type="text" inputMode="numeric" maxLength={6} placeholder="Código 2FA (opcional)"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="font-mono"
            data-testid="totp-disable-token-input"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="destructive" onClick={disable} disabled={loading || !password} data-testid="totp-confirm-disable-btn">
            {loading ? "Desativando..." : "Confirmar desativação"}
          </Button>
          <Button variant="outline" onClick={() => { setStep("idle"); setPassword(""); setCode(""); }}>Cancelar</Button>
        </div>
      </div>
    );
  }

  if (step === "regenerating") {
    return (
      <div className="space-y-3 border border-border rounded p-4 bg-muted/10" data-testid="totp-regen-panel">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <RefreshCw className="size-4" /> Gerar novos códigos de recuperação
        </div>
        <p className="text-xs text-muted-foreground">Os códigos antigos serão invalidados. Informe senha + código atual do Authenticator.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Input type="password" placeholder="Senha atual" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="totp-regen-pwd-input" />
          <Input
            type="text" inputMode="numeric" maxLength={6} placeholder="Código 2FA"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="font-mono"
            data-testid="totp-regen-token-input"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={regenerate} disabled={loading || !password || code.length !== 6} data-testid="totp-confirm-regen-btn">
            {loading ? "Gerando..." : "Gerar novos códigos"}
          </Button>
          <Button variant="outline" onClick={() => { setStep("idle"); setPassword(""); setCode(""); }}>Cancelar</Button>
        </div>
      </div>
    );
  }

  // idle
  return (
    <div className="space-y-3" data-testid="totp-idle-panel">
      <div className={`flex items-center justify-between rounded border p-4 ${enabled ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20"}`}>
        <div className="flex items-center gap-3">
          {enabled ? <ShieldCheck className="size-6 text-primary" /> : <ShieldOff className="size-6 text-muted-foreground" />}
          <div>
            <div className="text-sm font-semibold">
              {enabled ? "2FA ativa" : "2FA desativada"}
            </div>
            <div className="text-xs text-muted-foreground">
              {enabled
                ? `Você tem ${recoveryRemaining} código(s) de recuperação restante(s).`
                : "Adicione uma camada extra de segurança usando o Google Authenticator."
              }
            </div>
          </div>
        </div>
        {!enabled ? (
          <Button onClick={begin} disabled={loading} data-testid="totp-begin-setup-btn">
            <ShieldCheck className="size-4 mr-1" /> Ativar 2FA
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setStep("regenerating")} data-testid="totp-regen-btn">
              <RefreshCw className="size-4 mr-1" /> Novos códigos
            </Button>
            <Button variant="outline" size="sm" onClick={() => setStep("disabling")} data-testid="totp-disable-btn">
              <ShieldOff className="size-4 mr-1" /> Desativar
            </Button>
          </div>
        )}
      </div>
      {!enabled && (
        <div className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Como funciona:</span> depois de ativar, todo login de admin exigirá a senha
          + um código de 6 dígitos gerado pelo Google Authenticator. Receberá 10 códigos de recuperação de uso único
          para caso perca o celular. Ao usar o fluxo <span className="italic">&quot;Esqueci minha senha&quot;</span> (pergunta
          de segurança), a 2FA é automaticamente desativada para permitir a recuperação.
        </div>
      )}
    </div>
  );
}
